import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getEnvironment } from '#src/store/environment/selectors';
import { ErrorBoundary } from '#src/components/ErrorBoundary';
import Loading from '#src/components/Loading';
import DynamicImage from '#src/components/DynamicImage';

import { CloudinaryChart } from './CloudinaryChart';
import styles from './styles.module.scss';

const ImageLoader = ({ src, ...imgProps }) => (
  <ErrorBoundary fallback={<CloudinaryChart />}>
    <Suspense fallback={<Loading className={styles.chartLoading} />}>
      <DynamicImage src={src} {...imgProps} />
    </Suspense>
  </ErrorBoundary>
);

// if we don't have onboarding information we'll use these
// to generate the chart without y-axis value labels.
const genericChartValues = {
  startWeight: 200,
  goalWeight: 185,
  style: 'generic',
};

export function WeightLossChart({
  startWeight,
  goalWeight,
  showNewChart,
  numMonths = 5,
  keepRatio = 0.5,
}) {
  const { WB_WEIGHTLOSS_CHART_BASE_URL, WB_WEIGHTLOSS_CHART_BASE_URL_V2 } =
    useSelector(getEnvironment);

  const baseUrl = showNewChart
    ? WB_WEIGHTLOSS_CHART_BASE_URL_V2
    : WB_WEIGHTLOSS_CHART_BASE_URL;

  const chartUrl = useMemo(() => {
    const useGeneric = !startWeight || !goalWeight;
    const url = baseUrl && new URL(baseUrl);
    Object.entries({
      startWeight,
      goalWeight,
      numMonths,
      keepRatio,
      ...(useGeneric ? genericChartValues : {}),
    }).forEach(([key, value]) => url?.searchParams.set(key, value));
    return url?.toString();
  }, [baseUrl, startWeight, goalWeight, numMonths, keepRatio]);

  return !chartUrl ? null : (
    <ImageLoader src={chartUrl} alt="weight loss projection chart" />
  );
}
